/**
 * useUserRole — V2.N.2
 *
 * Lit le rôle applicatif (`user_profiles.role`) du user connecté.
 * Retourne `'admin'`, `'user'` ou `null` (pas connecté / pas encore
 * chargé).
 *
 * Source de vérité : la migration 20260622_user_roles_rbac.sql ajoute
 * une colonne `role` à `user_profiles` (default `'user'`). Le rôle ne
 * peut être modifié que via les RPCs `promote_user_to_admin` /
 * `demote_user_from_admin` qui passent par SECURITY DEFINER. Le
 * trigger `prevent_role_self_escalation` bloque tout UPDATE direct.
 *
 * Le hook se branche sur le `authStore` Zustand existant (changement
 * de session = re-fetch du rôle automatiquement).
 *
 * Le rôle est mis en cache 60s pour éviter de tapper la DB à chaque
 * navigation. Le cache est invalidé au login/logout.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';

export type UserRole = 'admin' | 'user' | null;

interface CacheEntry {
  role: UserRole;
  expiresAt: number;
}

let cache: Map<string, CacheEntry> = new Map();
const CACHE_TTL_MS = 60_000;

function getCachedRole(userId: string): UserRole | undefined {
  const entry = cache.get(userId);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    cache.delete(userId);
    return undefined;
  }
  return entry.role;
}

function setCachedRole(userId: string, role: UserRole): void {
  cache.set(userId, { role, expiresAt: Date.now() + CACHE_TTL_MS });
}

export function invalidateRoleCache(userId?: string): void {
  if (userId) cache.delete(userId);
  else cache.clear();
}

export function useUserRole(): { role: UserRole; isAdmin: boolean; isLoading: boolean } {
  const { user, isAuthenticated } = useAuthStore();
  const [role, setRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !user?.id) {
      setRole(null);
      setIsLoading(false);
      return;
    }

    const cached = getCachedRole(user.id);
    if (cached !== undefined) {
      setRole(cached);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    (async () => {
      try {
        // La colonne `role` vient de la migration 20260622_user_roles_rbac.sql
        // et n'est pas encore dans les types Supabase générés — cast explicite.
        const { data, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle<{ role: string | null }>();

        if (cancelled) return;

        if (error) {
          setRole('user'); // safe default
        } else {
          const r: UserRole = data?.role === 'admin' ? 'admin' : 'user';
          setRole(r);
          setCachedRole(user.id, r);
        }
      } catch {
        if (!cancelled) setRole('user');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  return {
    role,
    isAdmin: role === 'admin',
    isLoading,
  };
}
